// ---------------------------------------------------------------------------
// Resources page — searchable reading collection.
//
// Source: reading-list brief of 23 October 2026 (55 photographed entries from
// the Schumacher bookshelf) plus the 10 earlier list titles that are not
// duplicated there (filed under "Wider reading and historical material").
//
// RULES (owner brief):
// - Author names are recorded exactly as photographed; surnames alone are
//   intentional. Where the author is not established, the field stays blank.
// - Two differently subtitled Transition Handbook records are intentionally
//   kept as separate entries until editions are confirmed.
// - HELD (unpublished): "Radical Abundance", "Replacing GDP", "The Resilience
//   Imperative" — awaiting confirmed first names.
// - HELD (editorial review queue, unpublished): "Food as commons" (provisional
//   Routledge Handbook of Food as a Commons match — retain original wording
//   until confirmed) and "commons.blog" (website lead supplied by William;
//   contents and ownership unverified).
// - No invented ISBNs, dates, covers, links, reviews or descriptions.
// - 25 October 2026 additions: five confirmed books (brief-supplied notes and
//   links, mapped onto the existing five theme filters) and one confirmed
//   article in the Articles section. No new cover images sourced; the
//   nine-cover slider is unchanged.
// ---------------------------------------------------------------------------

// Featured key resources interrupting the reading list (26 October 2026,
// owner-supplied covers and notes). afterIndex = show the box after the
// entry at that index in the unfiltered "All" view.
export const featuredResources = [
  {
    afterIndex: 8,
    label: "Key resource",
    title: "Bioregional Financing Facilities",
    authors: "Samantha Power and Leon Seefeld",
    note: "A practical guide to connecting community-led regeneration with finance, exploring how local governance, shared ownership and coordinated funding can support thriving places and economies.",
    cover: "/bioregional-financing-facilities.jpg",
    link: "https://www.biofi.earth/biofi-book",
    linkLabel: "Resource page",
  },
  {
    afterIndex: 19,
    label: "Key resource",
    title: "Reweaving the Tapestry of Tenure",
    authors: "Edited by John Emmeus Davis",
    note: "The stories of eight pioneers of the community land trust movement offer inspiration for securing land in community ownership and sustaining its benefits across generations.",
    cover: "/reweaving-tapestry-tenure.jpg",
    link: "https://www.cltweb.org/terra-nostra-press/terra-nostra-press/reweaving-the-tapestry-of-tenure/",
    linkLabel: "Resource page",
  },
  {
    afterIndex: 29,
    label: "Key resource",
    title: "The Localization Reader",
    authors: "Edited by Raymond De Young and Thomas Princen",
    note: "A great place to start exploring stronger local economies and resilient communities, this collection includes work by E.F. Schumacher, Wendell Berry, M. King Hubbert, Karen Litfin and David Hess. An exploration of how communities can meet more of their needs locally, strengthening resilience and rebuilding local economies as energy and resources become more constrained.",
    cover: "/localization-reader.jpg",
    link: "https://mitpress.mit.edu/9780262516877/the-localization-reader/",
    linkLabel: "Resource page",
  },
  {
    afterIndex: 38,
    label: "Key resource",
    title: "Free, Fair and Alive: The Insurgent Power of the Commons",
    authors: "David Bollier and Silke Helfrich",
    note: "An inspiring introduction to the commons from two pioneering thinkers in the movement, exploring how people can share resources, govern collectively and build economies grounded in cooperation, care and mutual responsibility.",
    cover: "/free-fair-and-alive.jpg",
    link: "https://www.freefairandalive.org/",
    linkLabel: "Read online",
  },
  {
    afterIndex: 55,
    label: "Key resource",
    title: "Schumacher Center for a New Economics",
    authors: "",
    note: "A valuable starting point for exploring community land trusts, local currencies and community-supported enterprise, bringing together pioneering thinkers and practical projects that are shaping more just and resilient local economies.",
    cover: "/schumacher-center.jpg",
    coverAlt: "Schumacher Center for a New Economics logo",
    link: "https://centerforneweconomics.org/",
    linkLabel: "Visit website",
  },
  {
    afterIndex: 68,
    label: "Key resource",
    title: "The Change Handbook",
    authors: "Edited by Peggy Holman, Tom Devane and Steven Cady",
    note: "Brings together more than 60 methods for engaging people in shaping change across communities and organisations. A practical resource for designing participatory gatherings, choosing approaches suited to the situation and turning shared ideas into sustained action.",
    cover: "/change-handbook.jpg",
    link: "https://thechangehandbook.com/",
    linkLabel: "Visit website",
  },
];

export const resourcesPage = {
  title: "A learning library for land, community and local economies",
  booksHeading: "Educational resources for land, community and local livelihoods",
  intro:
    "Explore books and practical resources on land stewardship, community ownership, local economies and organising together. Browse by theme or search for a title.",
  closingPrompt: "Turning ideas into a practical project?",
  closingBody:
    "Talk with Bioregional Ventures about land access, community governance, project development or funding preparation.",
  ctaLabel: "Discuss your project",
  noResults: "No titles match — try a different search or clear the filters.",
  articlesHeading: "Articles",
};

// --- Book cover slider -------------------------------------------------------
// Covers supplied by the owner (publisher-sourced JPEGs in /public/covers).
// Slides appear in the brief's order; listTitle must match an entry in `books`
// exactly so the slider can jump to it. Covers marked null are awaiting the
// owner's upload — they render once a file is provided.
export const bookshelf = {
  heading: "From the bookshelf",
  intro: "",
};

export const bookshelfSlides = [
  {
    title: "Theory U",
    author: "C. Otto Scharmer",
    cover: "/covers/theory_u.jpg",
    listTitle: "Theory U: Leading from the Future as It Emerges",
  },
  {
    title: "How to Fall in Love with the Future",
    author: "Rob Hopkins",
    cover: "/covers/how_to_fall_in_love_with_the_future.jpg",
    listTitle: "How to Fall in Love with the Future",
  },
  { title: "Community, Third Edition", author: "Peter Block", cover: "/covers/community_third_edition.jpg", listTitle: "Community: The Structure of Belonging" },
  { title: "The Art of Belonging", author: "Hugh Mackay", cover: "/covers/the_art_of_belonging.webp", listTitle: "The Art of Belonging" },
  { title: "Walk Out Walk On", author: "Margaret J. Wheatley and Deborah Frieze", cover: "/covers/walk_out_walk_on.jpg", listTitle: "Walk Out Walk On" },
  {
    title: "The Wealth of the Commons",
    author: "David Bollier and Silke Helfrich, editors",
    cover: "/covers/wealth_of_the_commons.jpg",
    listTitle: "The Wealth of the Commons: A World Beyond Market and State",
  },
  {
    title: "Engaging Emergence",
    author: "Peggy Holman",
    cover: "/covers/engaging_emergence.jpg",
    listTitle: "Engaging Emergence",
  },
  { title: "Doughnut Economics", author: "Kate Raworth", cover: "/covers/doughnut_economics.jpg", listTitle: "Doughnut Economics" },
];

// Practical themes first; wider reading stays in its own theme.
export const themes = [
  "Community, citizenship and local livelihoods",
  "Land, commons and regenerative economics",
  "Facilitation, governance and collective action",
  "Resilience, transition and futures",
  "Wider reading and historical material",
];

const T = themes;

export const books = [
  // --- Community, citizenship and local livelihoods ---
  { title: "The Art of Belonging", author: "Hugh Mackay", theme: T[0] },
  { title: "Citizens", author: "Jon Alexander with Ariane Conrad", theme: T[0] },
  { title: "Creating Social Capital", author: "", theme: T[0] },
  { title: "The Book of the New Alchemists", author: "Deidre Williams", theme: T[0] },
  { title: "Community: The Structure of Belonging", author: "Peter Block", theme: T[0] },
  { title: "Walk Out Walk On", author: "Margaret Wheatley and Deborah Frieze", theme: T[0] },
  { title: "How to Be a Citizen", author: "C. L. Skach", theme: T[0] },
  { title: "The Art of Social Enterprise", author: "Frankel and Bromberger", theme: T[0] },
  { title: "Selling Local", author: "", theme: T[0] },
  { title: "Together We Can", author: "Claire O'Rourke", theme: T[0] },
  { title: "The Man Who Planted Trees", author: "Giono", theme: T[0] },
  // Added 25 October 2026 (confirmed books; notes and links supplied by brief):
  { title: "For All the People: Uncovering the Hidden History of Cooperation, Cooperative Movements, and Communalism in America", author: "John Curl", theme: T[0], note: "Cooperatives and economic history. The linked PDF is the 2009 PM Press edition, as supplied.", link: "https://library.uniteddiversity.coop/Cooperatives/For_All_The_People-History_of_Cooperation_in_America.pdf", linkLabel: "Read PDF" },
  { title: "Everything for Everyone: The Radical Tradition That Is Shaping the Next Economy", author: "Nathan Schneider", theme: T[0], note: "Cooperatives and the next economy — explores cooperative enterprise and democratic ownership.", link: "https://www.boldtypebooks.com/titles/nathan-schneider/everything-for-everyone/9781568589602/", linkLabel: "Resource page" },

  // --- Land, commons and regenerative economics ---
  { title: "Reweaving the Tapestry of Tenure", author: "John Emmeus Davis, editor", theme: T[1], link: "https://www.cltweb.org/terra-nostra-press/terra-nostra-press/reweaving-the-tapestry-of-tenure/", linkLabel: "Resource page" },
  // Added 26 October 2026 (owner-supplied cover and note; also featured in the list):
  { title: "The Localization Reader", author: "Raymond De Young and Thomas Princen, editors", theme: T[1], note: "A great place to start exploring stronger local economies and resilient communities, with work by E.F. Schumacher, Wendell Berry, M. King Hubbert, Karen Litfin and David Hess.", link: "https://mitpress.mit.edu/9780262516877/the-localization-reader/", linkLabel: "Resource page" },
  // Added 26 October 2026 (owner-supplied cover and note; also featured in the list):
  { title: "Free, Fair and Alive: The Insurgent Power of the Commons", author: "David Bollier and Silke Helfrich", theme: T[1], note: "An inspiring introduction to the commons from two pioneering thinkers in the movement.", link: "https://www.freefairandalive.org/", linkLabel: "Read online" },
  // Added 26 October 2026 (owner-supplied cover; also on the bookshelf carousel):
  { title: "The Wealth of the Commons: A World Beyond Market and State", author: "David Bollier and Silke Helfrich, editors", theme: T[1], link: "https://wealthofthecommons.org/", linkLabel: "Read online" },
  // Added 26 October 2026 (owner-supplied cover and note; also featured above the list):
  { title: "Bioregional Financing Facilities", author: "Samantha Power and Leon Seefeld", theme: T[1], note: "A practical guide to connecting community-led regeneration with finance, exploring how local governance, shared ownership and coordinated funding can support thriving places and economies.", link: "https://www.biofi.earth/biofi-book", linkLabel: "Resource page" },
  // Added 26 October 2026 (owner-supplied cover photo):
  { title: "The Community Land Trust Handbook", author: "Institute for Community Economics", theme: T[1], note: "Through land trusts, communities can regain control of local land and housing. This book tells how, for both rural and urban communities." },
  { title: "Doughnut Economics", author: "Kate Raworth", theme: T[1] },
  { title: "Small Is Beautiful: A Study of Economics as if People Mattered", author: "E. F. Schumacher", theme: T[1] },
  { title: "The Question of the Commons", author: "McCay and Acheson", theme: T[1] },
  { title: "The Blue Commons", author: "Guy Standing", theme: T[1] },
  { title: "Prosperity Without Growth", author: "Tim Jackson", theme: T[1] },
  { title: "The New Economy of Nature", author: "Daily and Ellison", theme: T[1] },
  { title: "Valuing Nature", author: "Ginn", theme: T[1] },
  { title: "Farming Democracy", author: "", theme: T[1] },
  { title: "Dwellers in the Land", author: "Kirkpatrick Sale", theme: T[1] },
  { title: "LifePlace: Bioregional Thought and Practice", author: "Thayer", theme: T[1] },
  { title: "Thinking Like a Mountain", author: "", theme: T[1] },
  { title: "The Great Awakening", author: "Grear and Bollier, editors", theme: T[1] },
  { title: "The Great Regeneration", author: "Cox", theme: T[1] },
  { title: "Muddling Toward Frugality", author: "Johnson", theme: T[1] },
  // Added 25 October 2026 (confirmed books; notes and links supplied by brief):
  { title: "Sharing Cities: Activating the Urban Commons", author: "Shareable, editor", theme: T[1], note: "Urban commons and community governance — a practical reference on shared urban resources and community-led solutions.", link: "https://www.shareable.net/programs/additional-programs/sharing-cities/", linkLabel: "Resource page" },
  { title: "Consulting the Genius of the Place: An Ecological Approach to a New Agriculture", author: "Wes Jackson", theme: T[1], note: "Land and food systems — considers agriculture in relation to natural ecosystems and the character of place.", link: "https://www.counterpointpress.com/books/consulting-the-genius-of-the-place/", linkLabel: "Resource page" },
  { title: "The New Systems Reader: Alternatives to a Failed Economy", author: "James Gustave Speth and Kathleen Courrier, editors", theme: T[1], note: "Economic systems and community wealth — an edited collection of proposals for systemic economic change.", link: "https://www.routledge.com/The-New-Systems-Reader-Alternatives-to-a-Failed-Economy/Courrier-Speth/p/book/9780367313395", linkLabel: "Resource page" },
  // Confirmed 25 October 2026 (was the provisional "Food as commons" lead):
  { title: "Routledge Handbook of Food as a Commons", author: "Jose Luis Vivero-Pol, Tomaso Ferrando, Olivier De Schutter and Ugo Mattei, editors", theme: T[1], note: "Food commons. Chapters 1 and 24 of this book are freely available as downloadable Open Access PDFs.", link: "https://www.routledge.com/Routledge-Handbook-of-Food-as-a-Commons/Vivero-Pol-Ferrando-DeSchutter-Mattei/p/book/9780367628567", linkLabel: "Resource page" },

  // --- Facilitation, governance and collective action ---
  { title: "Appreciative Inquiry Handbook: For Leaders of Change, 2nd Edition", author: "Cooperrider, Whitney and Stavros", theme: T[2] },
  { title: "Essentials of Appreciative Inquiry", author: "Cooperrider, Whitney and Stavros", theme: T[2] },
  { title: "The World Café", author: "Brown, Isaacs et al.", theme: T[2] },
  { title: "Calling the Circle", author: "Christina Baldwin", theme: T[2] },
  { title: "Finding Our Way", author: "Margaret J. Wheatley", theme: T[2] },
  { title: "Engaging Emergence", author: "Peggy Holman", theme: T[2] },
  { title: "Open Space Technology", author: "Owen", theme: T[2] },
  { title: "Theory U: Leading from the Future as It Emerges", author: "C. Otto Scharmer", theme: T[2] },
  { title: "People Power Manual 1: Campaign Strategy", author: "The Change Agency", theme: T[2] },
  { title: "People Power Manual 2: Community Organising", author: "The Change Agency", theme: T[2] },
  // Added 25 October 2026 (owner-supplied PDF, hosted locally):
  { title: "How to Create a Farming Co-operative", author: "BCCM and Ernst & Young", theme: T[2], note: "A practical step-by-step guide to establishing and operating a co-operative.", link: "/how-to-create-a-farming-cooperative.pdf", linkLabel: "Read PDF" },

  // --- Resilience, transition and futures ---
  { title: "The Knowledge: How to Rebuild Civilization in the Aftermath of a Cataclysm", author: "Lewis Dartnell", theme: T[3] },
  { title: "The Clock of the Long Now", author: "Brand", theme: T[3] },
  { title: "Peak Everything", author: "Richard Heinberg", theme: T[3] },
  { title: "The Transition Handbook: From Oil Dependency to Local Resilience", author: "Rob Hopkins", theme: T[3] },
  { title: "The Transition Handbook: Tools for Changing the World", author: "Rob Hopkins", theme: T[3] },
  { title: "How to Fall in Love with the Future", author: "Rob Hopkins", theme: T[3] },
  { title: "Regenerative Learning", author: "Satish Kumar and Lorna Howarth, editors", theme: T[3] },

  // --- Wider reading and historical material ---
  { title: "The Best of Resurgence", author: "John Button, editor", theme: T[4] },
  { title: "Small World Big Ideas", author: "Satish Kumar, editor", theme: T[4] },
  { title: "The Schumacher Lectures", author: "Edited with an introduction by Satish Kumar", theme: T[4] },
  { title: "E. F. Schumacher", author: "Barbara Wood", theme: T[4] },
  { title: "Spiritual Capital", author: "Danah Zohar and Ian Marshall", theme: T[4] },
  { title: "The Findhorn Garden", author: "The Findhorn Community", theme: T[4] },
  { title: "A Way of Working", author: "D. M. Dooling, editor", theme: T[4] },
  { title: "Memories & Visions of Paradise", author: "Richard Heinberg", theme: T[4] },
  { title: "Truth or Dare", author: "Starhawk", theme: T[4] },
  { title: "50th Anniversary of the World Game Institute 1972–2022: Memories from Former Staff", author: "", theme: T[4] },
  // Earlier list titles not duplicated in the photographed catalogue:
  { title: "Spinoza in the Anthropocene", author: "Genevieve Lloyd", theme: T[4] },
  { title: "Mountains and Rivers Without End", author: "Gary Snyder", theme: T[4] },
  { title: "Hallo witte mensen", author: "Anousha Nzume", theme: T[4] },
  { title: "Braiding Sweetgrass", author: "Robin Wall Kimmerer", theme: T[4] },
  { title: "The Sun My Heart", author: "Thich Nhat Hanh", theme: T[4] },
  { title: "The Best of Times, The Worst of Times", author: "Paul Behrens", theme: T[4] },
  { title: "Regenerative Economics", author: "John B. Fullerton", theme: T[4] },
  { title: "The Production of Money", author: "Ann Pettifor", theme: T[4] },
  { title: "The Good Ancestor", author: "Roman Krznaric", theme: T[4] },
  { title: "We Are Free to Change the World", author: "Lyndsey Stonebridge", theme: T[4] },
];

// --- Articles ---------------------------------------------------------------
// Confirmed article (brief, 25 October 2026). Articles are not books — they
// stay in this section rather than the catalogue.
export const articles = [
  {
    title: "Why Food Should Be a Commons Not a Commodity",
    author: "José Luis Vivero Pol",
    theme: "Food commons",
    note: "Article published by United Nations University's Our World.",
    link: "https://ourworld.unu.edu/en/why-food-should-be-a-commons-not-a-commodity",
    linkLabel: "Resource page",
  },
];

// --- Websites ---------------------------------------------------------------
// Website lead supplied by William, confirmed 25 October 2026. Listed as a
// website with no author or description (contents unverified — do not expand).
// --- Books by Dr. Vandana Shiva (26 October 2026, owner-supplied catalogue
// from Navdanya - The Reading Room; collection codes omitted per owner) ------
export const vandanaShiva = {
  heading: "Books by Dr. Vandana Shiva",
  source: "Navdanya · The Reading Room",
  intro: "Twenty-eight books and reports by Dr. Vandana Shiva.",
  bio: "Dr Vandana Shiva is an Indian environmental thinker, author and activist whose work connects biodiversity, food sovereignty and farmers' rights. Trained in physics, she founded Navdanya, a movement supporting seed saving, organic farming and the protection of agricultural diversity. Her writing challenges corporate control of seeds and food systems, advocating ecological agriculture grounded in local knowledge and community stewardship.",
  bijaVidyapeeth:
    "At Bija Vidyapeeth—Earth University, on Navdanya's biodiversity farm near Dehradun, India, participants can explore seed saving, agroecology, biodiversity and living food systems through courses combining discussion with practical learning.",
  bijaLinkLabel: "Explore courses at Bija Vidyapeeth",
  bijaLink: "https://navdanya.org/bija-vidyapeeth/",
  shelves: [
    {
      label: "Agriculture & Biodiversity",
      description: "Seed sovereignty, agroecology, biopiracy, and the case against monoculture.",
      titles: [
        { title: "Biodiversity, Agroecology, Regenerative Organic Agriculture", year: "2018" },
        { title: "Biodiversity, Gender and Technology in Mountain Agriculture", year: "2005" },
        { title: "Biopiracy: The Plunder of Nature and Knowledge", year: "2012" },
        { title: "Soil Not Oil: Climate Change, Peak Oil, and Food Insecurity", year: "2008" },
        { title: "Climate Instability on Himalayan Ecosystems and Himalayan Communities", year: "2009" },
        { title: "Monocultures of the Mind", year: "2012" },
        { title: "Earth Democracy: Justice, Sustainability, and Peace", year: "2010" },
        { title: "Violence of the Green Revolution", year: "2010" },
        { title: "Staying Alive: Women, Ecology and Survival in India (2018 ed.)", year: "2018" },
        { title: "Women in the Vanguard", year: "2015" },
        { title: "Sacred Seed", year: "2014" },
        { title: "Oneness vs. the 1%", year: "2018" },
        { title: "Origin: The Corporate War on Nature and Culture", year: "2013" },
        { title: "Produce Your Own Seed", year: "2018" },
      ],
    },
    {
      label: "Food & Health",
      description: "Who feeds the world, what it costs the body, and food as a right and a medicine.",
      titles: [
        { title: "Stolen Harvest: The Hijacking of the Global Food Supply", year: "2018" },
        { title: "Seed Dictatorship and Food Fascism", year: "" },
        { title: "Making Peace With The Earth", year: "2012" },
        { title: "The Great Health Scam", year: "2016" },
        { title: "Who Really Feeds the World?", year: "2017" },
        { title: "Annam: Food as Health", year: "2017" },
        { title: "The Two Futures of Food, Health & Humanity — A Civilization Dialogue", year: "2020" },
        { title: "Rights for Planet Earth", year: "2018" },
        { title: "The Nature of Nature", year: "2024" },
        { title: "Annapurna: Solutions to Reset Earth's Metabolic Disorder", year: "2025" },
        { title: "Terra Viva", year: "2022" },
        { title: "Slow Living", year: "2026" },
      ],
    },
    {
      label: "Bhoomi Series",
      description: "The Himalaya as teacher — ecology, the sacred, and the divine feminine.",
      titles: [
        { title: "Bhoomi: Sacred Himalaya — The Divine Feminine", year: "2013" },
        { title: "Bhoomi: Listening to the Mountains", year: "2017" },
      ],
    },
  ],
};

export const websitesHeading = "Websites";

export const websites = [
  {
    title: "commons.blog",
    author: "",
    theme: "Website",
    link: "http://commons.blog",
    linkLabel: "Visit website",
  },
  {
    title: "The 4 Returns Framework",
    author: "Commonland",
    theme: "Website",
    note: "4 Returns in Practice: A Guidebook for Holistic Landscape Restoration brings together ecological recovery, community wellbeing and local livelihoods through four returns: inspiration, social, natural and financial. Developed by Commonland, this practical guide offers tools for building partnerships, shaping a shared vision and turning long-term landscape ambitions into coordinated local action.",
    link: "https://4returns.commonland.com/lesson/the-4-returns-framework",
    linkLabel: "Visit website",
  },
  {
    title: "Proeftuin voor Transitieversnelling",
    author: "",
    theme: "Website",
    note: "Amazing things happening in the Netherlands - a living lab for accelerating transition.",
    link: "https://proefdetransitie.nl/index.php/over-ons-proeftuin-voor-transitieversnelling/",
    linkLabel: "Visit website",
  },
  {
    title: "Schumacher Center for a New Economics",
    author: "",
    theme: "Website",
    note: "Community land trusts, local currencies and community-supported enterprise.",
    link: "https://centerforneweconomics.org/",
    linkLabel: "Visit website",
  },
];
